import * as Yup from "yup";
import { Days, PickedWeekDayTimes } from "./common";
import { MData } from "../components/mask-multi-select/types";
import { OptionData } from "../queries/use-specialities-list";
import { LocationHour } from "../services";
import { getOfficialTimes } from "../utils/date-time-utility";
import dayjs from "dayjs";

export type Address = {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  country?: string;
  zipcode?: string;
};

export type ProviderGroup = {
  uuid?: string;
  name: string;
  schema?: string;
  subdomain?: string;
  phone: string;
  npi?: string;
  federalTaxId?: string;
  email: string;
  website: string;
  fax: string;
  description: string;
  physicalAddress?: Address;
  billingAddress?: Address;
  active: boolean;
  archive?: boolean;
  avatar: string;
  timezone: string;
  group_npi?: string;

  officeHours?: PickedWeekDayTimes;
  specialities?: OptionData[];

  acceptedInsurances?: MData[];
};

/**
 * The provider-group form on its way to the API: `ProviderGroupDrawer` flattens
 * the week-day picker into the day rows the endpoints take before submitting.
 */
export type ProviderGroupSubmitValues = Omit<ProviderGroup, "officeHours"> & {
  officeHours?: LocationHour[];
};

export const INITIAL_ADDRESS: Address = {
  line1: "",
  line2: "",
  city: "",
  state: "",
  country: "",
  zipcode: "",
};

export const ZIP_CODE_REGEX = /^\d{5}(-\d{4})?$/;

export const ZIP_CODE_MESSAGE =
  "Enter a valid ZIP Code (e.g., 12345 or 12345-6789)";

// Physical address feeds the claim scrubber, so every part of it is mandatory.
export const REQUIRED_ADDRESS_SCHEMA = Yup.object({
  line1: Yup.string().trim().required().label("Address Line 1"),
  city: Yup.string().trim().required().label("City"),
  state: Yup.string().required().label("State"),
  country: Yup.string().required().label("Country"),
  zipcode: Yup.string()
    .required()
    .matches(ZIP_CODE_REGEX, ZIP_CODE_MESSAGE)
    .label("ZIP Code"),
});

const { startTime, endTime } = getOfficialTimes();

const initialOfficeHours = {
  open: dayjs(startTime).toISOString(),
  close: dayjs(endTime).toISOString(),
};

const INITIAL_OFFICEHOURS_ARRAY = Object.values(Days).reduce(
  (acc: PickedWeekDayTimes, cur: Days) => {
    acc[cur] = [initialOfficeHours];
    return acc;
  },
  {}
);

export const PROVIDER_GROUP_INITIAL_VALUES: ProviderGroup = {
  uuid: "",
  name: "",
  officeHours: INITIAL_OFFICEHOURS_ARRAY,
  // schema: "",
  subdomain: "",
  timezone: "",
  phone: "",
  npi: "",
  federalTaxId: "",
  email: "",
  website: "",
  fax: "",
  description: "",
  physicalAddress: INITIAL_ADDRESS,
  billingAddress: undefined,
  active: false, // Typically starts as inactive
  archive: false, // Assuming new records are not archived by default
  avatar: "",
  specialities: undefined,
  acceptedInsurances: undefined,
};

export const PROVIDER_GROUP_SCHEMA = Yup.object({
  website: Yup.string()
    .test("valid-website", "Please Enter a Valid Website", (value) => {
      if (!value) return true; // Skip validation if the field is empty (optional)
      const regex = /^((https?:\/\/)?www\.)[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\/.*)?$/;

      return regex.test(value) && !/[\s;]+/.test(value);
    })
    .label("Website")
    .optional(),
  subdomain: Yup.string().required().label("Sub Domain"),
  phone: Yup.string().min(12, "Contact Number must be at least 8 digits"),
  // picture: Yup.string(),
  name: Yup.string().required().label("Provider Group"),
  // phone: Yup.string(),
  // speciality: Yup.string(),
  timezone: Yup.string().required().label("Timezone"),
  npi: Yup.string()
    .required()
    .matches(/^\d{10}$/, {
      message: "Group NPI Number must be exactly 10 digits, numbers only",
      excludeEmptyString: true,
    })
    .label("Group NPI"),
  federalTaxId: Yup.string()
    .required()
    .matches(/^\d{9}$/, {
      message: "Federal Tax ID must be exactly 9 digits, numbers only",
      excludeEmptyString: true,
    })
    .label("Federal Tax ID"),
  // group_npi: Yup.string(),
  email: Yup.string()
    .email()
    .matches(/.*@.+\..+/, "Email address must be a valid Email")
    .label("Email")
    .optional(),
  fax: Yup.string().min(12, "Fax Number must be at least 8 digits"),
  physicalAddress: REQUIRED_ADDRESS_SCHEMA.required(),
  billingAddress: Yup.object({
    zipcode: Yup.string().matches(ZIP_CODE_REGEX, ZIP_CODE_MESSAGE),
  })
    .nullable()
    .optional(),
  // website: Yup.string(),
  // fax: Yup.string(),
  // description: Yup.string(),
  // physical_address: Yup.object({
  //   address_line_1: Yup.string(),
  //   address_line_2: Yup.string(),
  //   city: Yup.string(),
  //   state: Yup.string(),
  //   country: Yup.string(),
  //   zip_code: Yup.string(),
  // }),
  // billing_address: Yup.object({
  //   address_line_1: Yup.string(),
  //   address_line_2: Yup.string(),
  //   city: Yup.string(),
  //   state: Yup.string(),
  //   country: Yup.string(),
  //   zip_code: Yup.string(),
  // }),
  // office_hours: Yup.object(),
});

export type DepartmentInitialValuesType = {
  deptId?: string;
  adminId: string | null;
  name: string;
  contact: string;

  workLocations?: MData[];
  active?: boolean;
  archive?: boolean;
};

export const addDepartmentInitialValues: DepartmentInitialValuesType = {
  name: "",
  deptId: undefined,
  adminId: null,
  contact: "",
  workLocations: undefined,
};
export type AppointmentTypeType = {
  uuid?: string;
  typeName?: string;
  color?: string;
  duration?: string;
  autoConfirmOnSchedule: boolean;
  active?: boolean;
  archive?: boolean;
  intakeForms?: { uuid: string; name: string }[];
  sla?: string;
};
export const addAppointmentTypeInitialValues: AppointmentTypeType = {
  typeName: undefined,
  autoConfirmOnSchedule: false,
  color: undefined,
  duration: undefined,
  intakeForms: undefined,
  sla: undefined,
};

export const SLA_MIN_DAYS = 1;
export const SLA_MAX_DAYS = 30;
export const SLA_HELPER_TEXT = `Days before appointment date (${SLA_MIN_DAYS}-${SLA_MAX_DAYS})`;
export const SLA_NUMBER_MESSAGE = "SLA must be a number";
export const SLA_WHOLE_NUMBER_MESSAGE = "SLA must be a whole number";
export const SLA_MIN_MESSAGE = `SLA must be at least ${SLA_MIN_DAYS} day`;
export const SLA_MAX_MESSAGE = `SLA must not exceed ${SLA_MAX_DAYS} days`;

export const appointmentTypeValidationSchema = Yup.object({
  typeName: Yup.string()
    .trim("Appointment Type Name cannot be blank spaces")
    .strict(true)
    .max(100, "Appointment Type Name must not exceed 100 characters")
    .required()
    .label("Appointment Type Name"),
  // color: Yup.string().required().label("Color"),
  duration: Yup.number()
    // .typeError("Duration must be a number")
    // .min(5, "Duration must be at least 5 minutes")
    // .max(480, "Duration must not exceed 480 minutes")
    // .integer("Duration must be a whole number")
    // .test(
    //   "multiple-of-5",
    //   "Duration must be a multiple of 5",
    //   (value) => !value || value % 5 === 0
    // )
    .required()
    .label("Duration"),
  // intakeForms: Yup.string().required().label("Intake Forms & Consent"),
  // Key must stay `sla` to match the Formik field name, otherwise the rule never runs.
  sla: Yup.number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null ? undefined : value
    )
    .typeError(SLA_NUMBER_MESSAGE)
    .integer(SLA_WHOLE_NUMBER_MESSAGE)
    .min(SLA_MIN_DAYS, SLA_MIN_MESSAGE)
    .max(SLA_MAX_DAYS, SLA_MAX_MESSAGE)
    .label("SLA - Due Date of Submitting Intake Forms"),
});
